import { useEffect, useMemo, useState } from "react";
import { CalendarRange, Clock, Users, Trash2, Plus, ChevronDown, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { todayAST, fmtDate } from "@/lib/date";
import { useAuth } from "@/context/AuthContext";
import HospitalCombobox from "@/components/HospitalCombobox";

const today = todayAST;

const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
function weekRange(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const day = (d.getDay() + 6) % 7; // Monday = 0
  const start = new Date(d); start.setDate(d.getDate() - day);
  const end = new Date(start); end.setDate(start.getDate() + 6);
  const fmt = (x) => `${x.getDate()} ${MONTHS[x.getMonth()]}`;
  return { key: fmtDate(start), label: `Semana del ${fmt(start)} – ${fmt(end)}` };
}

export default function Availability() {
  const { user } = useAuth();
  const [blocks, setBlocks] = useState([]);
  const [form, setForm] = useState({
    date: today(), center_name: user?.doctor_profile?.center_name || "",
    start_time: "09:00", end_time: "12:00", modality: "HORAS", slot_minutes: 30, capacity: 20,
  });
  const [busy, setBusy] = useState(false);
  const [openWeeks, setOpenWeeks] = useState({});
  const [closedCenters, setClosedCenters] = useState({});

  const grouped = useMemo(() => {
    const weeks = {};
    for (const b of blocks) {
      const { key, label } = weekRange(b.date);
      weeks[key] = weeks[key] || { key, label, centers: {} };
      const c = b.center_name || "Sin centro";
      (weeks[key].centers[c] = weeks[key].centers[c] || []).push(b);
    }
    return Object.values(weeks)
      .sort((a, b) => a.key.localeCompare(b.key))
      .map((w) => ({
        ...w,
        centerList: Object.entries(w.centers)
          .map(([center, items]) => ({
            center,
            items: items.sort((x, y) => (x.date + x.start_time).localeCompare(y.date + y.start_time)),
          }))
          .sort((a, b) => a.center.localeCompare(b.center)),
      }));
  }, [blocks]);

  const load = async () => {
    const { data } = await api.get("/availability", { params: { doctor_id: user.user_id } });
    setBlocks(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (grouped.length) setOpenWeeks((prev) => (Object.keys(prev).length ? prev : { [grouped[0].key]: true }));
  }, [grouped]);

  const toggleWeek = (k) => setOpenWeeks((p) => ({ ...p, [k]: !p[k] }));
  const toggleCenter = (k) => setClosedCenters((p) => ({ ...p, [k]: !p[k] }));

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.center_name) { toast.error("Selecciona un hospital de la lista"); return; }
    setBusy(true);
    try {
      await api.post("/availability", {
        ...form,
        slot_minutes: Number(form.slot_minutes),
        capacity: Number(form.capacity),
      });
      toast.success("Bloque creado");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    await api.delete(`/availability/${id}`);
    toast.success("Bloque eliminado");
    load();
  };

  return (
    <div>
      <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Disponibilidad</h1>
      <p className="mt-1 text-sm font-semibold text-slate-500">Define bloques por hora fija (HORAS) o por llegada (CUPOS).</p>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <form onSubmit={submit} className="lg:col-span-1 space-y-4 rounded-2xl border border-[#E2E8F0] bg-white p-6">
          <div className="flex items-center gap-2 text-sm font-extrabold text-slate-900"><Plus className="h-4 w-4 text-[#0284C7]" /> Nuevo bloque</div>
          <Field label="Fecha"><Input type="date" min={today()} value={form.date} onChange={set("date")} className="rounded-xl border-[#E2E8F0]" data-testid="av-date" /></Field>
          <Field label="Centro médico"><HospitalCombobox value={form.center_name} onSelect={(h) => setForm((f) => ({ ...f, center_name: h.name }))} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Inicio"><Input type="time" value={form.start_time} onChange={set("start_time")} className="rounded-xl border-[#E2E8F0]" data-testid="av-start" /></Field>
            <Field label="Fin"><Input type="time" value={form.end_time} onChange={set("end_time")} className="rounded-xl border-[#E2E8F0]" data-testid="av-end" /></Field>
          </div>
          <Field label="Modalidad">
            <Select value={form.modality} onValueChange={(v) => setForm((f) => ({ ...f, modality: v }))}>
              <SelectTrigger className="rounded-xl border-[#E2E8F0] font-bold" data-testid="av-modality"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="HORAS">Hora fija (HORAS)</SelectItem>
                <SelectItem value="CUPOS">Por llegada (CUPOS)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {form.modality === "HORAS" ? (
            <Field label="Minutos por cita"><Input type="number" min={5} value={form.slot_minutes} onChange={set("slot_minutes")} className="rounded-xl border-[#E2E8F0]" data-testid="av-minutes" /></Field>
          ) : (
            <Field label="Cupos totales"><Input type="number" min={1} value={form.capacity} onChange={set("capacity")} className="rounded-xl border-[#E2E8F0]" data-testid="av-capacity" /></Field>
          )}
          <Button type="submit" disabled={busy} className="w-full rounded-full bg-[#0284C7] font-bold hover:bg-[#0369a1]" data-testid="av-submit">
            {busy ? "Guardando…" : "Crear bloque"}
          </Button>
        </form>

        <div className="lg:col-span-2 space-y-3">
          {blocks.length === 0 && (
            <div className="rounded-2xl border border-dashed border-[#E2E8F0] bg-white p-10 text-center">
              <CalendarRange className="mx-auto h-8 w-8 text-slate-300" />
              <p className="mt-3 text-sm font-bold text-slate-500">Aún no has creado bloques de disponibilidad.</p>
            </div>
          )}

          {grouped.map((w) => {
            const weekOpen = !!openWeeks[w.key];
            const weekCount = w.centerList.reduce((n, c) => n + c.items.length, 0);
            return (
              <div key={w.key} className="overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white" data-testid={`week-group-${w.key}`}>
                <button type="button" onClick={() => toggleWeek(w.key)} className="flex w-full items-center justify-between px-5 py-3.5 text-left hover:bg-slate-50" data-testid={`week-toggle-${w.key}`}>
                  <span className="flex items-center gap-2 text-sm font-extrabold text-slate-900">
                    <CalendarRange className="h-4 w-4 text-[#0284C7]" /> {w.label}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="rounded-full border border-[#E2E8F0] px-2.5 py-0.5 text-xs font-bold text-slate-500">{weekCount} bloques</span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${weekOpen ? "rotate-180" : ""}`} />
                  </span>
                </button>

                {weekOpen && (
                  <div className="border-t border-[#E2E8F0] p-3 space-y-2">
                    {w.centerList.map((c) => {
                      const ckey = `${w.key}::${c.center}`;
                      const centerOpen = !closedCenters[ckey];
                      return (
                        <div key={ckey} className="rounded-xl border border-[#E2E8F0]" data-testid={`center-group-${ckey}`}>
                          <button type="button" onClick={() => toggleCenter(ckey)} className="flex w-full items-center justify-between rounded-xl bg-slate-50 px-4 py-2.5 text-left hover:bg-slate-100" data-testid={`center-toggle-${ckey}`}>
                            <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-600">
                              <Building2 className="h-3.5 w-3.5 text-[#0284C7]" /> {c.center}
                            </span>
                            <span className="flex items-center gap-2">
                              <span className="text-xs font-bold text-slate-400">{c.items.length}</span>
                              <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${centerOpen ? "rotate-180" : ""}`} />
                            </span>
                          </button>
                          {centerOpen && (
                            <div className="space-y-2 p-2">
                              {c.items.map((b) => (
                                <div key={b.id} className="flex items-center justify-between rounded-xl border border-[#E2E8F0] bg-white p-4" data-testid={`avblock-${b.id}`}>
                                  <div className="flex items-center gap-4">
                                    <div className={`flex h-10 w-10 items-center justify-center rounded-full ${b.modality === "HORAS" ? "bg-sky-50 text-[#0284C7]" : "bg-emerald-50 text-emerald-600"}`}>
                                      {b.modality === "HORAS" ? <Clock className="h-4 w-4" /> : <Users className="h-4 w-4" />}
                                    </div>
                                    <div>
                                      <div className="text-sm font-extrabold text-slate-900">{b.date} · {b.start_time}–{b.end_time}</div>
                                      <div className="text-xs font-bold text-slate-400">
                                        {b.modality === "HORAS" ? `${b.slot_minutes} min/cita` : `${b.capacity} cupos`}
                                      </div>
                                    </div>
                                  </div>
                                  <button onClick={() => del(b.id)} className="rounded-full p-2 text-red-400 hover:bg-red-50" data-testid={`del-block-${b.id}`}><Trash2 className="h-4 w-4" /></button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
